import { UploadFile } from "@mui/icons-material";
import {
	Box,
	Button,
	CircularProgress,
	Stack,
	TextareaAutosize,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import { ChangeEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {deleteAttachedFile, loadFileInfo} from "../../../actions/form";
import { sendReply } from "../../../actions/util";
import getBackendURL from "../../../api/api";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { download, request } from "../../../request";
import { ESRState } from "../../../types/data";

export const NewCommentEntry = ({ threadId , questionId}: { threadId: number, questionId?: string | null | undefined }) => {
	const dispatch = useDispatch();
	const { esrId = "", activeLabel = "" ,activeId = "" } = useParams();
	const [user, setUser] = useCurrentUser();
	const [message, setMessage] = useState("");

	const [loading, setLoading] = useState(false);
	const [fileIds, setFileIds] = useState<string[]>([]);
	const filenames = useSelector((state: ESRState) => state.filenames);

	const uploadFiles = async (e: ChangeEvent<HTMLInputElement>) => {
		if (!user) return;
		if (!e.target.files) return;
		setLoading(true);
		setFileIds([]);
		const responses = await Promise.all(
			Array.from(e.target.files).map((file) => {
				return request.postForm<{ file_id: string }>(
					getBackendURL("/esr/uploadfile/"),
					{
						esr_id: esrId,
						responder_id: user.email,
						addendum_label: activeLabel,
						question_id: (questionId !== undefined && questionId !== null) ? questionId : '',
						file,
					},
				);
			}),
		);
		const fileIds = responses.map((res) => res.data.file_id);
		for (const fileId of fileIds) {
			dispatch(loadFileInfo(activeId, fileId));
		}
		e.target.value = ""; // clear file list
		setFileIds(fileIds);
		setLoading(false);
	};

	const saveMessage = () => {
		if (user) {
			dispatch(sendReply(activeId, threadId, message, fileIds));
			setFileIds([]);
			setMessage("");
		}
	};

    const handleAttachedFileDeletion = async (fileId: string) => {
      if (!user) return;
      try {
        await dispatch(deleteAttachedFile(esrId, fileId, user.email));
        setFileIds((prev) => prev.filter((id) => id !== fileId));
      } catch (e) {
        console.error("Delete failed", e);
      }
    };

	const showSendButton = message || fileIds.length > 0;

	return (
		<Box
			sx={{
				boxShadow: "0px 0px 6px 2px #003E621F",
				px: "24px",
				pb: "10px",
				"&  .MuiInput-root": {
					height: 54,
				},
				background: "white",
			}}
		>
			<TextareaAutosize
				placeholder="Reply"
				style={{
					margin: "16px 0",
					width: "100%",
					resize: "none",
					fontSize: "16px",
					fontFamily: "Roboto, sans-serif",
					background: "none",
					border: "none",
					outline: "none",
				}}
				value={message}
				onChange={(e) => setMessage(e.target.value)}
			/>
			<Box>
				<Stack direction="row" sx={{ my: "8px", flexWrap: "wrap", gap: "4px" }}>
					{fileIds.map((fileId) => (
						<Chip
							key={fileId}
							label={filenames?.[fileId]}
							onClick={() =>
								download(
									getBackendURL(
										`/esr/attachment/?esr_id=${esrId}&file_id=${fileId}`,
									),
								)
							}
                            onDelete={() => handleAttachedFileDeletion(fileId)}
						/>
					))}
				</Stack>
				<Button
					component="label"
					variant="text"
					startIcon={loading ? <CircularProgress size={20} /> : <UploadFile />}
				>
					Attach file
					<input
						type="file"
						multiple
						style={{
							clipPath: "inset(50%)",
							height: 1,
							width: 1,
						}}
						onChange={uploadFiles}
					/>
				</Button>
			</Box>
			{showSendButton ? (
				<Box sx={{ display: "flex", p: "24px" }}>
					<Box sx={{ flex: 1 }} />
					<Button variant="contained" onClick={saveMessage}>
						Send
					</Button>
				</Box>
			) : null}
		</Box>
	);
};
